import { type FieldValue } from "~/components/job_posts/field";
import type { EeocQuestion, Question } from "~/types/jben/job_post";
import { invalidProfile, WEBSITE_FIELD_LABELS } from "../util";
import type { QuickApplyProfile, Resume, TrackingMetadata } from "../types";
import { type EducationAutofiller } from "./education_autofiller";
import { type EmploymentAutofiller } from "./employment_autofiller";
import type { Dispatch, SetStateAction } from "react";
import rollbar from "~/utils/rollbar.client";
import type { TrackEventType } from "~/hooks/use_snowplow_tracker";
import { SnowplowEvent } from "~/hooks/use_snowplow_tracker";

export class Autofiller {
  private answers: Record<string, FieldValue | null>;
  private setAnswers: (answers: Record<string, FieldValue>) => void;
  private questions: Question[];
  private eeocQuestions: EeocQuestion[];

  private educationAutofiller: EducationAutofiller;
  private employmentAutoFiller: EmploymentAutofiller;
  private trackEvent: TrackEventType;
  private metadata?: TrackingMetadata;

  constructor(
    answers: Record<string, FieldValue>,
    setAnswers: (answers: Record<string, FieldValue>) => void,
    questions: Question[],
    eeocQuestions: EeocQuestion[],
    educationAutoFiller: EducationAutofiller,
    employmentAutoFiller: EmploymentAutofiller,
    trackEvent: TrackEventType,
    metadata?: TrackingMetadata
  ) {
    this.answers = answers;
    this.setAnswers = setAnswers;
    this.questions = questions;
    this.eeocQuestions = eeocQuestions;

    this.educationAutofiller = educationAutoFiller;
    this.employmentAutoFiller = employmentAutoFiller;
    this.metadata = metadata;
    this.trackEvent = trackEvent;
  }

  async fill(
    profile: QuickApplyProfile,
    setStatus: Dispatch<SetStateAction<"success" | "error" | null>>
  ): Promise<void> {
    if (invalidProfile(profile)) {
      setStatus("error");
      rollbar.error("Invalid profile for autofill", { profile });
      return;
    }

    try {
      this.fillAnswers(profile);
      this.employmentAutoFiller.fill(profile);
      await this.educationAutofiller.fill(profile);
      this.trackSuccess();
      setStatus("success");
    } catch (e: any) {
      rollbar.error("Failed to autofill", e);
      setStatus("error");
    }
  }

  private fillAnswers(profile: QuickApplyProfile) {
    const newAnswers = {
      ...this.answers,
      first_name: this.chooseAnswer("first_name", profile.firstName),
      last_name: this.chooseAnswer("last_name", profile.lastName),
      email: this.chooseAnswer("email", profile.email),
      phone: this.chooseAnswer("phone", profile.phoneNumber),
      resume: this.chooseResumeAnswer(profile.resume),
    } as typeof this.answers;

    // Location is not always included in the form, so we should only set it if the field exists
    if (this.hasLocationField) {
      newAnswers.location = this.chooseAnswer("location", profile.location?.displayName);
      newAnswers.latitude = this.chooseAnswer("latitude", profile.location?.latitude.toString());
      newAnswers.longitude = this.chooseAnswer("longitude", profile.location?.longitude.toString());
      newAnswers.country_short_name = this.chooseAnswer(
        "country_short_name",
        profile.location?.countryShortName
      );
    }

    // Preferred name is not always included in the form, so we should only set it if the field exists
    if (this.hasPreferredNameField) {
      newAnswers.preferred_name = this.chooseAnswer("preferred_name", profile.preferredName);
    }

    this.fillWebsites(profile, newAnswers);

    this.fillEeoc(profile, newAnswers);

    this.setAnswers(newAnswers);
  }

  private chooseAnswer(key: string, autofillAnswer?: string | null) {
    const existingAnswer = this.answers[key];

    if (typeof existingAnswer === "object" && existingAnswer != null) {
      return existingAnswer;
    }

    return existingAnswer?.trim() || autofillAnswer || "";
  }

  private fillWebsites(profile: QuickApplyProfile, newAnswers: Record<string, FieldValue>): void {
    const websites = profile.websites;

    if (!websites) {
      return;
    }

    (Object.keys(websites) as (keyof typeof websites)[]).forEach((key) => {
      const question = this.findQuestionByTitles(WEBSITE_FIELD_LABELS[key]);
      if (question) {
        const questionKey = question.fields[0].name;
        newAnswers[questionKey] = this.chooseAnswer(questionKey, websites[key]);
      }
    });
  }

  private fillEeoc(profile: QuickApplyProfile, newAnswers: Record<string, FieldValue>): void {
    const selfIdentification = profile.selfIdentification;

    if (!selfIdentification) {
      return;
    }

    this.eeocQuestions.forEach((question) => {
      const field = question.fields[0];

      switch (field.name) {
        case "disability_status":
          newAnswers.disability_status = this.chooseAnswer(
            "disability_status",
            selfIdentification.disabilityStatus?.toString()
          );
          break;
        case "gender":
          newAnswers.gender = this.chooseAnswer("gender", selfIdentification.gender?.toString());
          break;
        case "race":
          newAnswers.race = this.chooseAnswer("race", selfIdentification.race?.toString());
          break;
        case "veteran_status":
          newAnswers.veteran_status = this.chooseAnswer(
            "veteran_status",
            selfIdentification.veteranStatus?.toString()
          );
          break;
      }
    });
  }

  private findQuestionByTitles(titles: string[]): Question | undefined {
    const lowercaseTitles = titles.map((title) => title.toLowerCase());
    return this.questions.find((question) =>
      lowercaseTitles.includes(question.label.toLowerCase())
    );
  }

  private chooseResumeAnswer(resume: Resume): FieldValue {
    if (!resume) {
      return this.answers.resume;
    }

    if (this.answers.resume || this.answers.resume_text) {
      return this.answers.resume;
    }

    return resume;
  }

  private get hasLocationField(): boolean {
    return this.questions.some((question) => question.fields[0]?.name === "candidate_location");
  }

  private get hasPreferredNameField(): boolean {
    return this.questions.some((question) => question.fields[0]?.name === "preferred_name");
  }

  private trackSuccess(): void {
    if (!this.metadata) {
      return;
    }

    this.trackEvent(SnowplowEvent.QuickApplyAutofillSuccess, {
      job_post_id: this.metadata.jobPostId,
    });
  }
}
